import { memo, useCallback, useMemo, useState } from 'react'

import { AddCircle, CalendarMonth, ContentCopy, Event, OpenInNew } from '@mui/icons-material'
import { LoadingButton } from '@mui/lab'
import {
  Alert,
  Box,
  Button,
  FormControlLabel,
  MenuItem,
  Paper,
  Skeleton,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import { useInfiniteQuery, useQuery } from '@tanstack/react-query'
import { add, formatISO } from 'date-fns'
import { AnimatePresence, motion } from 'motion/react'
import CopyToClipboard from 'react-copy-to-clipboard'
import { useTranslation } from 'react-i18next'

import { CodeInline } from '@shared/components/MaterialUIEnhancements/CodeInline'
import { popInOut } from '@shared/utils/support'
import {
  buildSubscriptionUrl,
  buildWebcalUrl,
  CALENDAR_TOKEN_SCOPE,
  GOOGLE_CALENDAR_ADD_BY_URL,
} from 'domains/Workspaces/Settings/Calendar/subscriptionUrl'
import { TokenRow } from 'domains/Workspaces/Settings/Calendar/TokenRow'
import { usePersonalAccessTokenCreateMutation } from 'hooks/mutations/identity/User'
import { workspaceSettingsRoute } from 'pages/NWPlatform/Workspaces/Workspace/Settings/Routing'
import { workspaceQueryKeys } from 'services/queryKeys'
import { userQueryKeys } from 'services/queryKeys/identity'

/**
 * A feed that stops working is worse than one that lives long: the calendar app fails quietly and the
 * events simply stop appearing. Hence no expiry by default - the link is revocable right here.
 */
const EXPIRY_OPTIONS = [
  { labelKey: 'domain:Workspace.Settings.Calendar.Expiry.never', value: 0 },
  { labelKey: 'domain:Workspace.Settings.Calendar.Expiry.ninetyDays', value: 90 },
  { labelKey: 'domain:Workspace.Settings.Calendar.Expiry.oneYear', value: 365 },
] as const

const OverviewRaw = () => {
  const { t } = useTranslation(['common', 'domain'])

  const { environmentId, workspaceId } = workspaceSettingsRoute.useParams()

  const [onCallOnly, setOnCallOnly] = useState(false)
  const [expiryDays, setExpiryDays] = useState<number>(0)
  const [token, setToken] = useState<string | null>(null)
  const [copied, setCopied] = useState(false)

  const workspaceQuery = useQuery({
    ...workspaceQueryKeys.detail({ workspaceId }),
    select: (data) => ({ name: data.name }),
  })

  const tokensQuery = useInfiniteQuery({
    // One page deliberately: a link the filter would find on page five is a link nobody can act on.
    ...userQueryKeys.personalAccessTokenList({ userId: 'my', limit: 100 }),
    select: (data) =>
      data.pages
        .flatMap((page) => page.items)
        .filter((item) => !item.allowFullAccess && item.scopes.includes(CALENDAR_TOKEN_SCOPE)),
  })

  const createMutation = usePersonalAccessTokenCreateMutation()

  const handleGenerate = useCallback(() => {
    const now = new Date()

    createMutation.mutate(
      {
        userId: 'my',
        personalAccessTokenInsert: {
          // Names the workspace, not the filter: the same token serves an on call link and a full one,
          // since only the URL decides which of the two it fetches.
          name: t('domain:Workspace.Settings.Calendar.linkName', { workspace: workspaceQuery.data?.name ?? '' }),
          validFrom: formatISO(now),
          expiryTime: expiryDays > 0 ? formatISO(add(now, { days: expiryDays })) : null,
          allowFullAccess: false,
          scopes: [CALENDAR_TOKEN_SCOPE],
        },
      },
      {
        onSuccess: (data) => {
          setToken(data.token!)
          setCopied(false)
        },
      },
    )
  }, [createMutation, expiryDays, t, workspaceQuery.data?.name])

  const handleCopy = useCallback(() => {
    setCopied(true)
    setTimeout(() => setCopied(false), 3000)
  }, [])

  const subscriptionUrl = useMemo(
    () => (token ? buildSubscriptionUrl({ environmentId, workspaceId, onCallOnly, token }) : null),
    [environmentId, onCallOnly, token, workspaceId],
  )

  /**
   * Google's dialog has no way to be handed the URL, so the next best thing is that the only step it
   * leaves is a paste. Neither call is awaited before the other: the clipboard write needs the click's
   * user gesture and a popup blocker would kill a window opened after an await.
   */
  const handleAddInGoogle = useCallback(() => {
    if (!subscriptionUrl) {
      return
    }

    navigator.clipboard?.writeText(subscriptionUrl).then(handleCopy, () => {
      // Denied clipboard permission is survivable - the Copy button is right above the dialog link.
    })

    window.open(GOOGLE_CALENDAR_ADD_BY_URL, '_blank', 'noreferrer')
  }, [handleCopy, subscriptionUrl])

  return (
    <Box
      sx={{ width: '100%' }}
      component={motion.div}
      variants={popInOut}
      initial="hidden"
      animate="visible"
      exit="hidden"
    >
      <Typography variant="heading-base">{t('domain:Workspace.Settings.Calendar.title')}</Typography>
      <Typography sx={{ mb: 2 }} variant="text">
        {t('domain:Workspace.Settings.Calendar.description')}
      </Typography>

      <Paper sx={{ bgcolor: 'background.levels.500', p: 1.5, mb: 2 }}>
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <FormControlLabel
              control={<Switch size="small" checked={onCallOnly} onChange={(e) => setOnCallOnly(e.target.checked)} />}
              label={<Typography variant="text">{t('domain:Workspace.Settings.Calendar.onCallOnly')}</Typography>}
            />
            <Typography variant="text-sm" color="text.secondary" component="p">
              {t('domain:Workspace.Settings.Calendar.onCallOnlyDescription')}
            </Typography>
          </Box>

          <TextField
            sx={{ width: 180 }}
            size="small"
            select
            label={t('domain:Workspace.Settings.Calendar.expiryLabel')}
            value={expiryDays}
            onChange={(e) => setExpiryDays(Number(e.target.value))}
          >
            {EXPIRY_OPTIONS.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {t(option.labelKey)}
              </MenuItem>
            ))}
          </TextField>

          <LoadingButton
            variant="outlined"
            size="small"
            endIcon={<AddCircle />}
            loadingPosition="end"
            loading={createMutation.isPending}
            disabled={!workspaceQuery.isSuccess}
            onClick={handleGenerate}
          >
            {t('domain:Workspace.Settings.Calendar.generate')}
          </LoadingButton>
        </Stack>
      </Paper>

      <AnimatePresence>
        {!!subscriptionUrl && (
          <Alert sx={{ mb: 2 }} severity="success" onClose={() => setToken(null)}>
            <Typography variant="heading-sm" sx={{ mb: 0.5 }}>
              {t('domain:Workspace.Settings.Calendar.linkCreated')}
            </Typography>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <CodeInline sx={{ flex: 1, py: 0.25 }} noWrap>
                {subscriptionUrl}
              </CodeInline>
              <CopyToClipboard text={subscriptionUrl} onCopy={handleCopy}>
                <Tooltip title={t('domain:Workspace.Settings.Calendar.copyLink')}>
                  <Button size="small" startIcon={<ContentCopy fontSize="small" />}>
                    {copied
                      ? t('domain:Workspace.Settings.Calendar.copied')
                      : t('domain:Workspace.Settings.Calendar.copy')}
                  </Button>
                </Tooltip>
              </CopyToClipboard>
            </Box>

            <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
              <Button
                size="small"
                variant="outlined"
                startIcon={<CalendarMonth fontSize="small" />}
                endIcon={<OpenInNew fontSize="small" />}
                onClick={handleAddInGoogle}
              >
                {t('domain:Workspace.Settings.Calendar.addToGoogle')}
              </Button>
              <Button
                size="small"
                variant="outlined"
                startIcon={<Event fontSize="small" />}
                href={buildWebcalUrl(subscriptionUrl)}
              >
                {t('domain:Workspace.Settings.Calendar.addToDefaultApp')}
              </Button>
            </Stack>

            <Typography variant="text-sm" component="p" sx={{ mb: 0.5 }}>
              {t('domain:Workspace.Settings.Calendar.googleHint')}
            </Typography>
            <Typography variant="text-sm">{t('domain:Workspace.Settings.Calendar.linkIsACredential')}</Typography>
          </Alert>
        )}
      </AnimatePresence>

      {createMutation.isError && <Alert severity="error">{t('somethingWentWrong')}</Alert>}

      <Typography variant="heading-sm">{t('domain:Workspace.Settings.Calendar.existingLinks')}</Typography>
      <Typography sx={{ mb: 1 }} variant="text-sm" color="text.secondary" component="p">
        {t('domain:Workspace.Settings.Calendar.existingLinksDescription')}
      </Typography>

      {tokensQuery.isLoading && (
        <Stack spacing={1}>
          <Skeleton height={48} />
          <Skeleton height={48} />
        </Stack>
      )}

      {tokensQuery.isSuccess &&
        (tokensQuery.data.length ? (
          <Paper sx={{ bgcolor: 'background.levels.500' }} variant="outlined">
            {tokensQuery.data.map((item) => (
              <TokenRow key={item.id} token={item} />
            ))}
          </Paper>
        ) : (
          <Typography variant="text" color="text.secondary">
            {t('domain:Workspace.Settings.Calendar.noLinks')}
          </Typography>
        ))}

      {tokensQuery.isError && <Alert severity="error">{t('somethingWentWrong')}</Alert>}
    </Box>
  )
}

export const Overview = memo(OverviewRaw)
